import React from 'react';
import { Message } from '../types';

interface ChannelBadgeProps {
  channel: Message['channel'];
  // Compact = icon + short label, lighter padding — for dense message rows.
  // Default (non-compact) = the full pill used in conversation-card headers.
  compact?: boolean;
  className?: string;
}

// A labeled channel pill: text + icon + color (never color alone — accessibility).
// EMAIL = blue envelope "Email"; SMS = green chat-bubble "SMS"; CALL = purple
// phone "Call" (8x8 call transcripts in the unified feed). Same size/shape for
// all three. Icon paths mirror the app's existing outline-icon language. Sizing
// matches the app's other small badges (e.g. Needs Review: text-[10px]
// sm:text-xs, whitespace-nowrap). Unknown values fall back to the SMS look
// (matches the pre-CALL behavior of the old binary check).
const ChannelBadge: React.FC<ChannelBadgeProps> = ({ channel, compact = false, className = '' }) => {
  const isEmail = channel === 'EMAIL';
  const isCall = channel === 'CALL';
  const label = isEmail ? 'Email' : isCall ? 'Call' : 'SMS';
  const colors = isEmail
    ? 'bg-blue-100 text-blue-800'
    : isCall
      ? 'bg-purple-100 text-purple-800'
      : 'bg-green-100 text-green-800';
  const icon = isEmail ? (
    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ) : isCall ? (
    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );

  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full font-semibold ${colors} ${
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[10px] sm:text-xs'
      } ${className}`}
    >
      {icon}
      {label}
    </span>
  );
};

export default ChannelBadge;
